import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';

import { useResolvedTheme } from '../theme';
import Modal, { Button, Field, Segmented, Toggle, inputClass } from './Modal';
import { useRhythmActions } from './data';
import type { Preferences } from '../types';
import type { Rhythm, RhythmDraft, RhythmKind, RhythmPeriod, RhythmStatus } from './types';
import { PERIOD_LABEL, SWATCHES, WEEKDAYS, cadenceLabel, hm, hoursLabel, tint } from './util';

interface Props {
  rhythm?: Rhythm;
  status?: RhythmStatus;
  onClose: () => void;
  onBack?: () => void;
}

const BLANK: RhythmDraft = {
  name: '',
  color: SWATCHES[0],
  kind: 'count',
  period: 'week',
  target: 1,
  duration_minutes: 60,
  max_per_day: 1,
  days: [0, 1, 2, 3, 4, 5, 6],
  window_start: '08:00',
  window_end: '22:00',
  prefers_dry: false,
  social: false,
  match_terms: [],
  task_tags: [],
  active: true
};

export default function RhythmEditor({ rhythm, status, onClose, onBack }: Props) {
  const theme = useResolvedTheme();
  const qc = useQueryClient();
  const weekStartsOn = qc.getQueryData<Preferences>(['preferences'])?.week_starts_on ?? 0;
  const weekOrder = Array.from({ length: 7 }, (_, i) => (i + weekStartsOn) % 7);
  const { create, update, remove } = useRhythmActions();
  const [d, setD] = useState<RhythmDraft>(() => {
    if (!rhythm) return BLANK;
    const { id: _id, position: _p, ...rest } = rhythm;
    return rest;
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = <K extends keyof RhythmDraft>(k: K, v: RhythmDraft[K]) => setD((x) => ({ ...x, [k]: v }));

  const windowMinutes = hm(d.window_end) - hm(d.window_start);
  const problem = !d.name.trim()
    ? 'Give it a name.'
    : d.days.length === 0
    ? 'Pick at least one day.'
    : windowMinutes <= 0
    ? 'The window has to end after it starts.'
    : windowMinutes < d.duration_minutes
    ? 'A block has to fit inside the window.'
    : null;

  const mutation = rhythm ? update : create;
  async function save() {
    if (problem) return;
    if (rhythm) await update.mutateAsync({ id: rhythm.id, ...d });
    else await create.mutateAsync(d);
    (onBack ?? onClose)();
  }

  const t = tint(d.color, theme);

  return (
    <Modal
      title={rhythm ? d.name || 'Rhythm' : 'New rhythm'}
      subtitle={rhythm ? cadenceLabel({ ...rhythm, ...d }) : undefined}
      accent={t.solid}
      onClose={onClose}
      onBack={onBack}
      width={580}
      footer={
        <>
          {rhythm &&
            (confirmDelete ? (
              <Button variant="danger" onClick={() => remove.mutate(rhythm.id, { onSuccess: onBack ?? onClose })}>
                Delete with its history
              </Button>
            ) : (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Delete…
              </Button>
            ))}
          <span className="flex-1" />
          {problem && <span className="text-[0.78rem] text-[var(--text-muted)]">{problem}</span>}
          <Button variant="ghost" onClick={onBack ?? onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!!problem || mutation.isPending} onClick={save}>
            {rhythm ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <input
            data-autofocus={!rhythm || undefined}
            value={d.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Name"
            className="w-full border-b border-[var(--border-strong)] bg-transparent pb-2 text-[1.2rem] font-semibold outline-none placeholder:text-[var(--text-faint)] focus:border-[var(--accent)]"
          />
          <div className="flex flex-wrap gap-[6px]">
            {SWATCHES.map((hex) => {
              const st = tint(hex, theme);
              return (
                <button
                  key={hex}
                  type="button"
                  aria-label={`colour ${hex}`}
                  onClick={() => set('color', hex)}
                  className={clsx(
                    'h-[22px] w-[22px] rounded-full transition-transform hover:scale-110',
                    d.color === hex && 'ring-2 ring-[var(--text)] ring-offset-2 ring-offset-[var(--popover-bg)]'
                  )}
                  style={{ background: st.solid }}
                />
              );
            })}
          </div>
        </div>

        <Section title="How much">
          <Segmented<RhythmKind>
            value={d.kind}
            onChange={(k) =>
              setD((x) => ({
                ...x,
                kind: k,
                period: k === 'hours' && x.period === 'day' ? 'week' : x.period
              }))
            }
            options={[
              { value: 'count', label: 'Sessions' },
              { value: 'hours', label: 'Hours' }
            ]}
          />
          <div className="flex flex-wrap items-end gap-3">
            <Field label={d.kind === 'hours' ? 'Hours' : 'Times'}>
              <input
                type="number"
                min={d.kind === 'hours' ? 0.5 : 1}
                step={d.kind === 'hours' ? 0.5 : 1}
                value={d.target}
                onChange={(e) => set('target', Number(e.target.value))}
                className={clsx(inputClass, 'w-[90px] tabular-nums')}
              />
            </Field>
            <Field label="Every">
              <select
                value={d.period}
                onChange={(e) => set('period', e.target.value as RhythmPeriod)}
                className={clsx(inputClass, 'w-[130px]')}
              >
                {(Object.keys(PERIOD_LABEL) as RhythmPeriod[])
                  .filter((p) => d.kind === 'count' || p !== 'day')
                  .map((p) => (
                    <option key={p} value={p}>
                      {PERIOD_LABEL[p]}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={d.kind === 'hours' ? 'Block length' : 'Length'}>
              <select
                value={d.duration_minutes}
                onChange={(e) => set('duration_minutes', Number(e.target.value))}
                className={clsx(inputClass, 'w-[110px]')}
              >
                {[15, 30, 45, 60, 90, 120, 150, 180, 240, 300].map((m) => (
                  <option key={m} value={m}>
                    {m < 60 ? `${m} min` : hoursLabel(m)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Most per day">
              <select
                value={d.max_per_day}
                onChange={(e) => set('max_per_day', Number(e.target.value))}
                className={clsx(inputClass, 'w-[80px]')}
              >
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Section>

        <Section title="When">
          <div className="flex gap-[6px]">
            {weekOrder.map((i) => {
              const label = WEEKDAYS[i];
              const on = d.days.includes(i);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => set('days', on ? d.days.filter((x) => x !== i) : [...d.days, i].sort())}
                  className={clsx(
                    'h-9 flex-1 rounded-md text-[0.8rem] transition-colors',
                    on
                      ? 'font-medium'
                      : 'border border-[var(--border)] text-[var(--text-faint)] hover:text-[var(--text-muted)]'
                  )}
                  style={on ? { background: t.soft, color: 'var(--text)', border: `1px solid ${t.line}` } : undefined}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="flex items-end gap-3">
            <Field label="From">
              <input type="time" step={900} value={d.window_start} onChange={(e) => set('window_start', e.target.value)} className={clsx(inputClass, 'w-[120px]')} />
            </Field>
            <Field label="Until">
              <input type="time" step={900} value={d.window_end} onChange={(e) => set('window_end', e.target.value)} className={clsx(inputClass, 'w-[120px]')} />
            </Field>
          </div>
          <div className="flex flex-col">
            <Toggle checked={d.prefers_dry} onChange={(v) => set('prefers_dry', v)} label="Prefers dry weather" />
            <Toggle checked={d.social} onChange={(v) => set('social', v)} label="With other people" />
            <Toggle checked={d.active} onChange={(v) => set('active', v)} label="Planning" />
          </div>
        </Section>

        <Section title="Counts from the calendar">
          <Chips
            values={d.match_terms}
            onChange={(v) => set('match_terms', v)}
            placeholder="Add a word"
            fixed={d.name.trim() || undefined}
          />
        </Section>

        <Section title="Obsidian to-dos">
          <Chips values={d.task_tags} onChange={(v) => set('task_tags', v.map((x) => x.replace(/^#/, '').toLowerCase()))} placeholder="Add a tag" prefix="#" />
        </Section>

        {status && <History status={status} rhythm={{ ...d, period: rhythm?.period ?? d.period }} color={t.solid} />}
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 text-[0.72rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">{title}</h3>
      {children}
    </section>
  );
}

function Chips({
  values,
  onChange,
  placeholder,
  prefix = '',
  fixed
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  prefix?: string;
  fixed?: string;
}) {
  const [draft, setDraft] = useState('');
  function add() {
    const v = draft.trim().replace(/^#/, '');
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  }
  return (
    <div className="flex flex-wrap items-center gap-[6px] rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-2 py-[6px] focus-within:border-[var(--accent)]">
      {fixed && (
        <span className="rounded bg-[var(--bg-hover)] px-2 py-[2px] text-[0.8rem] text-[var(--text-muted)]">{fixed}</span>
      )}
      {values.map((v) => (
        <span key={v} className="flex items-center gap-1 rounded bg-[var(--bg-hover)] px-2 py-[2px] text-[0.8rem]">
          {prefix}
          {v}
          <button
            type="button"
            aria-label={`remove ${v}`}
            onClick={() => onChange(values.filter((x) => x !== v))}
            className="text-[var(--text-faint)] hover:text-[var(--text)]"
          >
            ×
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !draft && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={add}
        placeholder={placeholder}
        className="min-w-[100px] flex-1 bg-transparent py-[2px] text-[0.85rem] outline-none placeholder:text-[var(--text-faint)]"
      />
    </div>
  );
}

const HISTORY_WORD: Record<RhythmPeriod, string> = {
  day: 'weeks',
  week: 'weeks',
  fortnight: 'fortnights',
  month: 'months'
};

function History({ status, rhythm, color }: { status: RhythmStatus; rhythm: RhythmDraft; color: string }) {
  const points = [
    ...status.history,
    { start: status.period_start, value: status.done, target: status.effective, tracked: true }
  ];
  const max = Math.max(1, ...points.map((p) => Math.max(p.value, p.target)));
  const minutes = status.unit === 'minutes';
  return (
    <Section title={`Last ${points.length} ${HISTORY_WORD[rhythm.period]}`}>
      <div className="flex h-[92px] items-end gap-[6px]">
        {points.map((p, i) => {
          const current = i === points.length - 1;
          const label = minutes ? hoursLabel(p.value) : String(p.value);
          return (
            <div key={p.start} className="flex h-full flex-1 flex-col items-center justify-end gap-1" title={`${format(parseISO(p.start), 'MMM d')}: ${label}`}>
              <div className="relative flex w-full flex-1 items-end">
                {p.target > 0 && (
                  <div
                    className="absolute inset-x-0 border-t border-dashed border-[var(--border-strong)]"
                    style={{ bottom: `${(p.target / max) * 100}%` }}
                  />
                )}
                <div
                  className={clsx('w-full rounded-t-[3px]', !p.tracked && 'opacity-40')}
                  style={{
                    height: `${Math.max(p.value ? 4 : 0, (p.value / max) * 100)}%`,
                    background: color,
                    opacity: current ? 0.6 : undefined
                  }}
                />
              </div>
              <span className={clsx('text-[0.66rem] tabular-nums', current ? 'text-[var(--text)]' : 'text-[var(--text-faint)]')}>
                {format(parseISO(p.start), rhythm.period === 'month' ? 'MMM' : 'd')}
              </span>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
